import { Scale } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { formatMoney, formatQty, parseMoney, type Product } from '@sense/shared';
import { useScale } from '../lib/scale';
import { Button, Kbd, Sheet } from './ui';
import { tr } from '../lib/i18n';

/** "250" typed in kilos means 250 g (nobody sells 250 kg loose); "2", "1.5" stay kilos. */
const gramsTyped = (v: string) => /^\d+$/.test(v.trim()) && Number(v) >= 50;

export const isMeasured = (unit: Product['unit']) => unit === 'kg' || unit === 'g' || unit === 'l' || unit === 'ml';
const big = (u: Product['unit']) => u === 'kg' || u === 'l';
const small = (u: Product['unit']) => (u === 'kg' || u === 'g' ? 'g' : 'ml');
const bigName = (u: Product['unit']) => (u === 'kg' || u === 'g' ? 'kg' : 'l');

/**
 * Weight / volume entry for loose items. Quantity is returned in the product's milli-units
 * (kg → 1.25 kg = 1250; g → 250 g = 250000). Works by weight, by ₹ amount, or from a connected scale.
 */
export function WeightDialog({ product, initial, onClose, onConfirm }: { product: Product | null; initial?: number; onClose(): void; onConfirm(qty: number): void }) {
  const scale = useScale();
  const [mode, setMode] = useState<'weight' | 'amount'>('weight');
  const [inBig, setInBig] = useState(true); // enter kg (or l) vs g (or ml)
  const [v, setV] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!product) return;
    setMode('weight');
    setError(null);
    const bigUnit = big(product.unit);
    setInBig(bigUnit);
    setV(initial ? String(bigUnit ? initial / 1000 : initial / 1000) : '');
  }, [product, initial]);

  // grams (or ml) entered so far
  const grams = useMemo(() => {
    if (!product) return NaN;
    const n = parseFloat(v.replace(',', '.'));
    if (mode === 'amount') {
      const paise = parseMoney(v);
      if (!Number.isFinite(paise) || product.price <= 0) return NaN;
      // price is per kg (or per g for gram-priced items)
      return big(product.unit) ? Math.round((paise * 1000) / product.price) : Math.round(paise / product.price);
    }
    if (!Number.isFinite(n)) return NaN;
    // In kg, a whole number of 50 or more is grams: "250" means 250 g, "1.25" or "2" mean kilos.
    if (inBig && gramsTyped(v)) return Math.round(n);
    return Math.round(inBig ? n * 1000 : n);
  }, [v, mode, inBig, product]);

  if (!product) return null;
  const toQty = (g: number) => (big(product.unit) ? g : g * 1000); // milli-units of the product's unit
  const scaleReady = scale.connected && scale.grams != null && scale.grams > 0;
  const effective = Number.isFinite(grams) && grams > 0 ? grams : scaleReady && !v ? scale.grams! : NaN;
  const qty = Number.isFinite(effective) ? toQty(effective) : NaN;
  const amount = Number.isFinite(qty) ? Math.round((product.price * qty) / 1000) : 0;

  const confirm = (q = qty) => {
    if (!Number.isFinite(q) || q <= 0) return setError('Enter the weight, an amount, or put the item on the scale.');
    if (q > (big(product.unit) ? 100_000 : 100_000_000)) return setError('That is more than 100 kg. Check the number.');
    onConfirm(q);
  };
  const quick = [100, 250, 500, 750, 1000, 2000];

  return (
    <Sheet open onClose={onClose} title={product.name}
      footer={<Button form="weight-form" size="lg" className="w-full">{tr('Add to bill{0}', { 0: Number.isFinite(qty) ? ` · ${formatMoney(amount)}` : '' })} <span className="hidden lg:inline"><Kbd>{tr('Enter')}</Kbd></span></Button>}>
      <form id="weight-form" onSubmit={(e) => { e.preventDefault(); confirm(); }} className="flex flex-col gap-4 pb-2">
        <div className="flex items-center justify-between">
          <span className="num text-sm text-muted">{formatMoney(product.price)} / {product.unit}</span>
          <div role="radiogroup" aria-label="Enter by" className="flex gap-1 rounded-full bg-forest-100 p-1">
            {([['weight', big(product.unit) || product.unit === 'g' ? tr('Weight') : tr('Volume')], ['amount', tr('₹ amount')]] as const).map(([k, l]) => (
              <button type="button" key={k} role="radio" aria-checked={mode === k} onClick={() => { setMode(k); setV(''); setError(null); }}
                className={`h-9 rounded-full px-4 text-sm font-bold ${mode === k ? 'bg-forest-700 text-white' : 'text-primary'}`}>{l}</button>
            ))}
          </div>
        </div>

        {scale.connected && (
          <button type="button" onClick={() => scaleReady && confirm(toQty(scale.grams!))}
            className={`flex items-center gap-3 rounded-3xl p-4 text-left ${scaleReady ? 'bg-forest-700 text-white' : 'bg-forest-50 text-primary'}`}>
            <Scale size={26} />
            <span className="flex-1">
              <span className="block text-xs font-bold opacity-80">{tr('ON THE SCALE {0}', { 0: scale.stable ? '' : tr('· settling…') })}</span>
              <span className="num block text-3xl font-semibold">{scale.grams == null ? '—' : scale.grams >= 1000 ? `${(scale.grams / 1000).toFixed(3)} ${bigName(product.unit)}` : `${scale.grams} ${small(product.unit)}`}</span>
            </span>
            {scaleReady && <span className="rounded-full bg-white px-4 py-2 text-sm font-bold text-primary">{tr('Use this')}</span>}
          </button>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-bold text-muted">{mode === 'amount' ? tr('Customer wants ₹ worth') : tr('Weight in {0}', { 0: inBig ? bigName(product.unit) : small(product.unit) })}</span>
          <span className="flex h-16 items-center gap-2 rounded-full border border-line bg-white pr-2 pl-5 focus-within:border-forest-600">
            <input autoFocus inputMode="decimal" value={v} onChange={(e) => { setV(e.target.value.replace(/[^\d.,]/g, '')); setError(null); }} onFocus={(e) => e.target.select()}
              aria-label={mode === 'amount' ? 'Amount in rupees' : 'Weight'}
              onKeyDown={(e) => {
                // Arrow keys nudge the weight; g / k switch units without leaving the keyboard.
                if (mode === 'weight' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                  e.preventDefault();
                  const cur = Number.isFinite(grams) ? grams : 0;
                  const next = Math.max(0, cur + (e.key === 'ArrowUp' ? 50 : -50));
                  setV(inBig ? String(+(next / 1000).toFixed(3)) : String(next));
                } else if (mode === 'weight' && (e.key === 'g' || e.key === 'k' || e.key === 'l' || e.key === 'm')) {
                  e.preventDefault();
                  setInBig(e.key === 'k' || e.key === 'l');
                }
              }}
              placeholder={scaleReady && mode === 'weight' ? tr('from scale') : '0'} className="num min-w-0 flex-1 bg-transparent text-3xl outline-none" />
            {mode === 'weight' && (
              <span className="flex gap-1 rounded-full bg-forest-100 p-1">
                {[true, false].map((b) => (
                  <button type="button" key={String(b)} aria-pressed={inBig === b} onClick={() => { setInBig(b); setV(''); }}
                    className={`h-10 rounded-full px-3.5 text-sm font-bold ${inBig === b ? 'bg-forest-700 text-white' : 'text-primary'}`}>{b ? bigName(product.unit) : small(product.unit)}</button>
                ))}
              </span>
            )}
          </span>
        </label>

        {mode === 'weight' && (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
            {quick.map((g) => (
              <button type="button" key={g} onClick={() => confirm(toQty(g))} className="num h-12 rounded-full border border-line bg-white text-sm font-semibold text-primary hover:bg-forest-50">
                {g >= 1000 ? `${g / 1000} ${bigName(product.unit)}` : `${g} ${small(product.unit)}`}
              </button>
            ))}
          </div>
        )}

        {mode === 'weight' && inBig && gramsTyped(v) && <div className="-mt-2 text-sm font-bold text-primary">{tr('Read as {0}', { 0: v })} {small(product.unit)}</div>}
        {mode === 'weight' && <p className="-mt-2 text-xs text-muted">{tr('Type 1.25 for {0}, or 250 for 250', { 0: `1.25 ${bigName(product.unit)}` })} {small(product.unit)} · ↑↓ ±50 {small(product.unit)} {tr('· Enter adds')}</p>}
        <div className="flex items-center justify-between rounded-3xl bg-forest-50 px-5 py-3">
          <span className="num text-sm font-semibold text-primary">{Number.isFinite(qty) ? `${formatQty(qty, product.unit)} × ${formatMoney(product.price)}` : tr('Amount')}</span>
          <span className="num text-2xl font-semibold">{formatMoney(amount)}</span>
        </div>
        {error && <div className="text-sm font-bold text-danger" role="alert">{error}</div>}
      </form>
    </Sheet>
  );
}
